/**
 * @file index.tsx
 *
 * @fileoverview Contains job application util functions.
 */

import React from 'react';
import { useIntl } from 'react-intl';
import { JobApplicationWorkflowEnum } from '~/jobiqo/components/JobApplication/enums';
import { getResumeTitle } from '~/jobiqo/components/Resume/utils';
import { Job, JobApplication, RecruiterStatus } from '~/jobiqo/types/types';
import { GDPRClearingStatusEnum } from '~/jobiqo/models/jobApplication';
import { getJobApplicationRecruiterStatusesTranslationsSlot } from '~/jobiqo/components/Slots/JobApplicationRecruiterStatusesTranslationsSlot';

/**
 * Constructs title based on resume academic prefix, first name, last name and
 * academic postfix or job application first name and last name.
 *
 * @param {JobApplication} jobApplication
 *  The job application.
 * @returns
 *  Job application title.
 */
export const getJobApplicationApplicantTitle = (
  jobApplication: JobApplication,
): string =>
  jobApplication.resume?.firstName
    ? getResumeTitle(jobApplication.resume)
    : `${jobApplication.firstName} ${jobApplication.lastName}`;

export function getJobApplicationRecruiterStatusesTranslations(): Record<
  RecruiterStatus,
  { label: React.ReactNode; tooltip: React.ReactNode }
> {
  const intl = useIntl();

  const statuses = {
    APPLIED: {
      label: intl.formatMessage({
        id: 'job_application_status_recruiter_applied',
        defaultMessage: 'Applied',
      }),
      tooltip: intl.formatMessage(
        {
          id: 'job_application_tooltip_recruiter_applied',
          defaultMessage: 'A candidate sent you{br}a job application',
        },
        { br: <br /> },
      ),
    },
    INTERVIEW: {
      label: intl.formatMessage({
        id: 'job_application_status_recruiter_interview',
        defaultMessage: 'Interview',
      }),
      tooltip: intl.formatMessage(
        {
          id: 'job_application_tooltip_recruiter_interview',
          defaultMessage: 'The job application{br}was marked for an interview',
        },
        { br: <br /> },
      ),
    },
    PROPOSED: {
      label: intl.formatMessage({
        id: 'job_application_status_recruiter_proposed',
        defaultMessage: 'Proposed',
      }),
      tooltip: intl.formatMessage(
        {
          id: 'job_application_tooltip_recruiter_proposed',
          defaultMessage:
            'The candidate fits{br}the job and was sent a job{br}proposal',
        },
        { br: <br /> },
      ),
    },
    REJECTED: {
      label: intl.formatMessage({
        id: 'job_application_status_recruiter_rejected',
        defaultMessage: 'Rejected',
      }),
      tooltip: intl.formatMessage(
        {
          id: 'job_application_tooltip_recruiter_rejected',
          defaultMessage:
            'Either the recruiter{br}or the candidate rejected{br}further cooperation',
        },
        { br: <br /> },
      ),
    },
    HIRED: {
      label: intl.formatMessage({
        id: 'job_application_status_recruiter_hired',
        defaultMessage: 'Hired',
      }),
      tooltip: intl.formatMessage(
        {
          id: 'job_application_tooltip_recruiter_hired',
          defaultMessage: 'The candidate was{br}successfully hired for the job',
        },
        { br: <br /> },
      ),
    },
    ARCHIVED: {
      label: intl.formatMessage({
        id: 'job_application_status_recruiter_archived',
        defaultMessage: 'Archived',
      }),
      tooltip: intl.formatMessage(
        {
          id: 'job_application_tooltip_recruiter_archived',
          defaultMessage:
            'The job application{br}is archived and data cannot{br}be accessed',
        },
        { br: <br /> },
      ),
    },
    IN_PROGRESS: {
      label: intl.formatMessage({
        id: 'job_application_status_recruiter_in_progress',
        defaultMessage: 'In Progress',
      }),
      tooltip: intl.formatMessage(
        {
          id: 'job_application_tooltip_recruiter_in_progress',
          defaultMessage:
            'The job application{br}is in progress and data can{br}be accessed',
        },
        { br: <br /> },
      ),
    },
    ANONYMIZED: {
      label: intl.formatMessage({
        id: 'job_application_status_recruiter_anonymize_manually',
        defaultMessage: 'Anonymized',
      }),
      tooltip: intl.formatMessage(
        {
          id: 'job_application_tooltip_recruiter_anonymize_manually',
          defaultMessage: 'The job application{br} is anonymized due the GDPR',
        },
        { br: <br /> },
      ),
    },
  };

  return getJobApplicationRecruiterStatusesTranslationsSlot(statuses);
}

/**
 * Checks if the job application workflow system is available
 * for the given job.
 *
 * @param {Job} job
 * The job to check the application workflow system.
 * @returns {boolean}
 * Returns true if the job application system is available.
 */
export function getJobApplicationSystemAvailable({
  applicationWorkflow,
  applicationLink,
}: Job) {
  // In the BE config, the application system can be set to "none".
  if (applicationWorkflow === JobApplicationWorkflowEnum.NONE) {
    return false;
  }
  // For some job imports, the job application system is set to "external",
  // but the URL for the application system could be empty.
  if (
    applicationWorkflow === JobApplicationWorkflowEnum.EXTERNAL &&
    !applicationLink
  ) {
    return false;
  }

  return true;
}

/**
 * If the email address, first name, and last name are all empty, then the job
 * application has been deleted.
 * @param {JobApplication} jobApplication - JobApplication - this is the object
 * that we're going to be checking to see if it's empty or not.
 * @param {boolean} JOB_APPLICATION_GDPR_ENABLED - boolean - this is a flag that
 * is set to true if the Jobiqo GDPR Job Application module is enabled and
 * job application data deletion is active.
 */
export const isJobApplicationDataDeleted = (
  jobApplication: JobApplication,
  JOB_APPLICATION_GDPR_ENABLED: boolean = false,
) =>
  JOB_APPLICATION_GDPR_ENABLED
    ? jobApplication['cleanupDueToGDPR'] == GDPRClearingStatusEnum.CLEARED
    : !jobApplication.emailAddress &&
      !jobApplication.firstName &&
      !jobApplication.lastName;
